import * as React from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import {
  getChartTypographyScale,
  getLegendSettings,
  getXScaleSettings,
  getYScaleSettingsTicks,
  scaleChartSize,
  yScaleSettings,
} from 'src/integration/settings/graphSettings';
import { spacerPlugin, yAxisTitlePlugin } from 'src/integration/settings/graphPlugins';

interface ComponentProps {
  data: any;
}

const StackedBarChart = ({ data }: ComponentProps) => {
  const chartTypographyScale = getChartTypographyScale();
  const hasAlignedDualXAxis = Boolean(data?.alignDualXAxis);
  const secondaryXAxisLabels = Array.isArray(data?.lineLabels) ? data.lineLabels : undefined;

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: getLegendSettings(chartTypographyScale),
      datalabels: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: {
          size: scaleChartSize(14, chartTypographyScale),
        },
        bodyFont: {
          size: scaleChartSize(14, chartTypographyScale),
        },
      },
      yAxisTitlePlugin: {
        label: 'EUR / MWh',
      },
    },
    scales: {
      x: {
        ...getXScaleSettings(chartTypographyScale),
        offset: !hasAlignedDualXAxis,
      },
      x1: {
        position: 'top',
        display: false,
        offset: !hasAlignedDualXAxis,
        labels: secondaryXAxisLabels,
        time: {
          unit: 'minute',
          displayFormats: { minute: 'HH:mm' },
        },
        grid: { drawTicks: false, drawOnChartArea: false },
      },
      y: {
        border: {
          dash: [3, 2],
        },
        ticks: {
          ...getYScaleSettingsTicks(chartTypographyScale),
        },
        ...yScaleSettings,
      },
      y1: {
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          ...getYScaleSettingsTicks(chartTypographyScale),
        },
      },
    },
  };

  const chartKey = React.useMemo(() => JSON.stringify(data), [data]);

  return (
    <Bar
      key={chartKey}
      data={data}
      options={options}
      plugins={[spacerPlugin, yAxisTitlePlugin]}
    />
  );
};

export default StackedBarChart;